import { memo } from "react";

import { Link } from "@react/components/Link";
import { Text } from "@react/components/Text";
import { tw } from "classnames";

type SegmentedControlOption =
  | {
      label: string;
      to: string;
      type: "link";
    }
  | {
      ariaLabel: string;
      label: string;
      onClick: () => void;
      type: "button";
    };

type Props = {
  linkAriaLabel?: string;
  options: SegmentedControlOption[];
  selectedIndex: number;
};

const styles = {
  container: (optionsLength: number) =>
    tw(
      {
        "grid-cols-1": optionsLength === 1,
        "grid-cols-2": optionsLength === 2 || optionsLength > 3,
        "grid-cols-3": optionsLength === 3,
      },
      "grid",
      "gap-2",
      "md:flex",
      "md:flex-nowrap",
      "w-full",
      "list-none",
      "m-0",
      "p-0"
    ),
  option: tw("md:flex-1"),
  link: (isSelected: boolean) =>
    tw(
      "flex",
      "items-center",
      "justify-center",
      "rounded",
      "border",
      "border-solid",
      "border-primary",
      "px-2",
      "py-2.5",
      "w-full",
      "box-border",
      "text-primary",
      "no-underline",
      "transition-colors",
      "focus:outline-none",
      "focus-visible:ring",
      "focus:ring-offset-1",
      "focus:ring-2",
      "focus:ring-interactive-outline",
      "cursor-pointer",
      {
        "bg-primary-inverse": !isSelected,
        "text-primary": !isSelected,
        "bg-primary": isSelected,
        "text-primary-inverse": isSelected,
        "hover:bg-primary": !isSelected,
        "hover:text-primary-inverse": !isSelected,
      }
    ),
};

const SegmentedControlBase = ({
  linkAriaLabel,
  options,
  selectedIndex,
}: Props) => {
  return (
    <ul className={styles.container(options.length)}>
      {options.map((option, index) => (
        <li
          className={styles.option}
          key={`${option.label}-${option.type}-${index}`}
        >
          {option.type === "button" ? (
            <button
              aria-label={option.ariaLabel}
              className={styles.link(selectedIndex === index)}
              onClick={option.onClick}
            >
              <Text as="body2">{option.label}</Text>
            </button>
          ) : (
            <Link
              aria-label={`${linkAriaLabel} ${option.to}`}
              className={styles.link(selectedIndex === index)}
              to={option.to}
              unstyled
            >
              <Text as="body2">{option.label}</Text>
            </Link>
          )}
        </li>
      ))}
    </ul>
  );
};

export const SegmentedControl = memo(SegmentedControlBase);
